import { Box, Typography } from "@mui/material";
import React, { useMemo } from "react";

import { monospaceStyle } from "../../theme";
import { dateRange } from "./format";

export interface SourceTimelineProps {
  // The publication date of every source, YYYY-MM-DD; an undated source
  // has no dot
  dates: (string | null | undefined)[];
}

// Diameter (px) of a dot
const DOT = 7;

// The years at either end: small, mono, in the faintest text tone
const yearSx = {
  ...monospaceStyle,
  fontSize: "0.65625rem",
  color: "text.disabled",
} as const;

/**
 * When the sources of an entity appeared: a thin axis with one dot per
 * dated source between the first and the latest date, and the year of each
 * end beneath. Renders nothing without a dated source.
 */
const SourceTimeline: React.FC<SourceTimelineProps> = ({ dates }) => {
  const range = useMemo(() => dateRange(dates), [dates]);

  const positions = useMemo(() => {
    if (!range) {
      return [];
    }
    const start = Date.parse(range.first);
    const span = Date.parse(range.latest) - start;
    return dates
      .filter((date): date is string => Boolean(date))
      .map((date) => ({
        date,
        // Every source on one day: the dot sits in the middle
        at: span > 0 ? (Date.parse(date) - start) / span : 0.5,
      }))
      .filter(({ at }) => Number.isFinite(at));
  }, [dates, range]);

  if (!range) {
    return null;
  }

  return (
    <Box
      role="img"
      aria-label={`Sources from ${range.first} to ${range.latest}`}
      sx={{ minWidth: 120 }}
    >
      <Box sx={{ position: "relative", height: DOT + 4, mx: `${DOT / 2}px` }}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            borderTop: 1,
            borderColor: "rgba(255, 255, 255, 0.14)",
          }}
        />
        {positions.map(({ date, at }, index) => (
          <Box
            key={`${date}-${index}`}
            title={date}
            sx={{
              position: "absolute",
              top: "50%",
              left: `${at * 100}%`,
              width: DOT,
              height: DOT,
              borderRadius: "50%",
              backgroundColor: "link.main",
              transform: "translate(-50%, -50%)",
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="caption" sx={yearSx}>
          {range.first.slice(0, 4)}
        </Typography>
        <Typography variant="caption" sx={yearSx}>
          {range.latest.slice(0, 4)}
        </Typography>
      </Box>
    </Box>
  );
};

export default SourceTimeline;
